import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  ElementRef,
  inject,
  signal,
  effect,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { AdminInboxNotification } from '@core/models/admin/account/admin-inbox.model';
import { AdminInboxService } from '@core/services/admin-inbox.service';
import { PushNotificationService } from '@core/services/push-notification.service';
import { SpinDirective } from '@shared/directives/button.directive';
import { ButtonDirective } from '@shared/directives/button.directive';

// Active resource_types from the backend:
//   ORDER      — order table
//   WITHDRAWAL — transactionLogger table (wallet transaction detail)
//   TRANSFER   — not yet emitted by backend; placeholder only
const ROUTES: Record<string, (id: string) => string> = {
  ORDER:      (id) => `/admin/orders/details/${id}`,
  WITHDRAWAL: (id) => `/admin/transactions/wallet/details/${id}`,
  // TRANSFER:   (id) => `/admin/transactions/wallet/details/${id}`,
};

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, SpinDirective, ButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './notification-bell.component.html',
})
export class NotificationBellComponent implements OnDestroy {
  readonly inbox = inject(AdminInboxService);
  private push = inject(PushNotificationService);
  private router = inject(Router);
  private host = inject(ElementRef);

  @ViewChild('inboxContainer') private inboxContainer?: ElementRef<HTMLElement>;
  @ViewChild('sentinel') private sentinel?: ElementRef<HTMLElement>;
  private observer: IntersectionObserver | null = null;

  private openObserver = effect(() => {
    if (this.open()) {
      setTimeout(() => this.setupObserver(), 0);
    } else {
      this.disconnectObserver();
    }
  });

  readonly open = signal(false);
  readonly enablingNotifications = signal(false);
  readonly notifications = this.inbox.notifications;
  readonly unreadCount = this.inbox.unreadCount;

  permissionGranted(): boolean {
    return (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    );
  }

  toggle(): void {
    const next = !this.open();
    this.open.set(next);
    if (next && this.permissionGranted()) {
      this.inbox.refresh();
    }
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    try {
      if (!this.inbox.hasMore() || this.inbox.loading()) return;
    } catch {
      return;
    }
    const threshold = 20;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.inbox.loadMore();
    }
  }

  private setupObserver(): void {
    try {
      if (!this.inboxContainer || !this.sentinel) return;
      this.disconnectObserver();
      const root = this.inboxContainer.nativeElement as HTMLElement;
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              try {
                if (this.inbox.hasMore() && !this.inbox.loading()) this.inbox.loadMore();
              } catch {}
            }
          }
        },
        { root, rootMargin: '0px', threshold: 0.1 }
      );
      this.observer.observe(this.sentinel.nativeElement);
    } catch {}
  }

  private disconnectObserver(): void {
    if (this.observer) {
      try {
        this.observer.disconnect();
      } catch {}
      this.observer = null;
    }
  }

  async enableNotifications(): Promise<void> {
    const permissionTask = this.push.requestPermissionAndRegister();
    this.enablingNotifications.set(true);
    await permissionTask;
    if (Notification.permission === 'granted') {
      window.location.reload();
      return;
    }
    this.enablingNotifications.set(false);
  }

  handleNotificationClick(n: AdminInboxNotification): void {
    this.inbox.markOneRead(n.id);
    this.open.set(false);

    const resourceType = n.resource_type ? n.resource_type.toUpperCase() : null;
    if (resourceType && n.resource_id) {
      const route = ROUTES[resourceType];
      if (route) {
        this.router.navigateByUrl(route(n.resource_id));
        return;
      }
    }

    const url = n.action1_url;
    if (url) {
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank');
      } else {
        this.router.navigateByUrl(url);
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target)) {
      this.open.set(false);
    }
  }

  ngOnDestroy(): void {
    this.disconnectObserver();
  }
}
